/**
 * TemplatePreviewDrawer – Full-screen drawer for previewing a template.
 *
 * Uses Medplum QuestionnaireForm runtime (useQuestionnaireForm hook) for:
 * - Page parsing / pagination (same rules as Medplum Forms)
 * - QuestionnaireResponse state management (in-memory only)
 * - Validation / required handling
 * - enableWhen conditional display
 *
 * Preserves our visual shell:
 * - Full-screen drawer
 * - Flat form layout (section headers + fields, no nested cards)
 * - Red * required indicators inline with titles
 * - Non-persistent (no QuestionnaireResponse saved to Medplum)
 */
import type { ReactNode } from 'react';
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Drawer,
  Group,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DateTimePicker, TimePicker } from '@mantine/dates';
import { UnifyDateInput } from '@/components/UnifyDateInput';
import { calendarDateFromPickerValue, parseFhirDateOrInstant } from '@/lib/dateHelpers';
import { isSignatureDateQuestion } from '@unify/fhir-model/signature-date';
import {
  IconArchive,
  IconCalendarEvent,
  IconChevronDown,
  IconClock,
  IconCopy,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import type { QuestionnaireFormLoadedState } from '@medplum/react-hooks';
import {
  isQuestionEnabled,
  QuestionnaireItemType,
  useQuestionnaireForm,
} from '@medplum/react-hooks';
import type { TemplateListItem } from '../types';
import {
  buildChoiceAnswerFromLabel,
  buildChoiceAnswersFromLabels,
  getAnswerOptionDisplayLabel,
} from '../utils/answerOptionLabel';
import { QuestionnaireTextEntry } from '@/components/questionnaire/QuestionnaireTextEntry';
import { buildQuestionnaireTextFieldKey } from '@/components/questionnaire/questionnaireTextFieldKey';
import {
  buildQuestionnaireItemMap,
  getChoiceOptionAddons,
  getOptionAddon,
  isChoiceOptionAddonVisibleNow,
} from '../utils/choiceOptionAddons';
import type { DocumentComment } from '../../../supervision/types';
import { CommentThreadBlock } from '../../../supervision/components/CommentThreadBlock';
import {
  resolveQuestionnaireVariables,
  resolveVariablesInText,
  useVariableContext,
} from '../../../../lib/template-variables';
import { useDocumentFormatPresentation } from '../hooks';
import {
  DocumentFormatFooter,
} from './DocumentFormatBranding';
import { hasSignatureRequired } from '../../../../lib/template-setup/questionnaire';
import { looksLikeHtml } from '../../../../lib/formResponseDisplay';
import { toFhirTimeValue } from '../../../../lib/fhirTimeValue';
import { sanitizeHtml } from '../../../../lib/template-setup/convert';
import { QUESTIONNAIRE_HIDDEN_EXT, SESSION_MODALITY_LINK_ID } from '@unify/fhir-model/constants';
import { upsertSessionModalityAnswer } from '../../../appointments/utils/sessionModalityPrefill';
import { ClientPortalSignatureSection } from '../../../../components/ClientPortalSignatureSection';
import { ChartContactPickerField } from '../../../../components/questionnaire/ChartContactPickerField';
import { CHART_CONTACT_PICKER_ITEM_CONTROL } from '../../../../lib/chart/coordinationOfCareRoi';
import { COORDINATION_OF_CARE_ROI_LINK_IDS } from '@unify/fhir-model/constants';
import type { SignatureInfo } from '../../../appointments/fhirNoteMapping';
import './TemplatePreviewDrawer.css';

const ITEM_CONTROL_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

/** Inner form controls use white background on the flat form surface. */
const PREVIEW_INPUT_BG = {
  backgroundColor: 'var(--mantine-color-white)',
  borderRadius: 'var(--mantine-radius-md)',
  borderColor: 'var(--mantine-color-gray-3)',
};
const PREVIEW_TEXTAREA_PLACEHOLDER = 'Type or dictate notes here...';

const FOLLOW_UP_FIELD_WRAPPER = { paddingLeft: 28, width: '100%', minWidth: 0 } as const;

/** Build slash-joined anchor path for a questionnaire item (matches QRDocumentWithComments). */
function buildItemPath(linkId: string | undefined, parentPath: string): string {
  const segment = linkId ?? '';
  return parentPath ? `${parentPath}/${segment}` : segment;
}

function getItemControl(item: QuestionnaireItem): string | undefined {
  return item.extension?.find((e) => e.url === ITEM_CONTROL_URL)?.valueCodeableConcept?.coding?.[0]?.code;
}

function isHiddenQuestionnaireItem(item: QuestionnaireItem): boolean {
  if (item.linkId === SESSION_MODALITY_LINK_ID) return true;
  return item.extension?.some((e) => e.url === QUESTIONNAIRE_HIDDEN_EXT && e.valueBoolean === true) === true;
}

function questionnaireHasLinkId(items: QuestionnaireItem[] | undefined, linkId: string): boolean {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return true;
    if (questionnaireHasLinkId(item.item, linkId)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// PreviewLabel – red * inline with title (our styled label)
// ---------------------------------------------------------------------------

/** Label matching StylingPage/theme form labels; spacing comes from PreviewQuestionShell. */
function PreviewLabel({ text, required }: { text: string; required?: boolean }): JSX.Element {
  return (
    <Text fw={600} size="sm" c="gray.8">
      {required && <Text span c="red.5" fw={700}>* </Text>}
      {text}
    </Text>
  );
}

function PreviewQuestionShell({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <Stack gap="xs" className="questionnaire-question">
      <PreviewLabel text={label} required={required} />
      <Box className="questionnaire-question-answer">{children}</Box>
    </Stack>
  );
}

function PreviewSectionGroup({
  title,
  required,
  children,
  depth = 0,
  instanceIndex,
}: {
  title: string;
  required?: boolean;
  children: ReactNode;
  /** Nesting depth (0 = top-level section). Kept for callers; no tree indent. */
  depth?: number;
  /** When set, appends " 1", " 2", … for repeating group instances. */
  instanceIndex?: number;
}): JSX.Element {
  const [opened, { toggle }] = useDisclosure(true);
  const sectionTitle = title.trim();
  const numberedTitle =
    instanceIndex != null && sectionTitle ? `${sectionTitle} ${instanceIndex + 1}` : sectionTitle;
  void depth;

  if (!sectionTitle) {
    return <Stack gap="lg">{children}</Stack>;
  }

  return (
    <Box className="questionnaire-tree-node" data-depth={depth}>
      <Stack gap="md" className="questionnaire-section">
        <Group justify="space-between" wrap="nowrap" align="center">
          <Text fw={700} size="md" c="dark" style={{ flex: 1, minWidth: 0 }}>
            {required && <Text span c="red.5" fw={700}>* </Text>}
            {numberedTitle}
          </Text>
          <UnstyledButton
            onClick={toggle}
            aria-expanded={opened}
            aria-label={opened ? 'Collapse section' : 'Expand section'}
            className="questionnaire-section-toggle"
          >
            <IconChevronDown
              size={16}
              className={opened ? 'questionnaire-section-chevron-open' : 'questionnaire-section-chevron-closed'}
            />
          </UnstyledButton>
        </Group>
        <Collapse in={opened}>
          <Stack gap="lg" pt="xs">
            {children}
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
}

function resolveSourceChoiceItem(
  item: QuestionnaireItem,
  questionnaireItemsByLinkId?: Map<string, QuestionnaireItem>,
): QuestionnaireItem {
  return item.linkId && questionnaireItemsByLinkId?.has(item.linkId)
    ? questionnaireItemsByLinkId.get(item.linkId)!
    : item;
}

function OptionAddonField({
  formState,
  context,
  item,
  responseItem,
  questionnaireItemsByLinkId,
  optionIndex,
  selectedAnswer,
  selectedAnswers,
}: {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  responseItem: QuestionnaireResponseItem | undefined;
  questionnaireItemsByLinkId?: Map<string, QuestionnaireItem>;
  optionIndex: number;
  selectedAnswer?: string;
  selectedAnswers?: string[];
}): JSX.Element | null {
  const sourceItem = resolveSourceChoiceItem(item, questionnaireItemsByLinkId);
  const addon = getOptionAddon(sourceItem, optionIndex);
  if (!addon) return null;

  const visible = isChoiceOptionAddonVisibleNow(
    sourceItem,
    addon,
    selectedAnswer,
    formState.questionnaireResponse,
    selectedAnswers,
  );
  if (!visible) return null;

  const nestedResponse = responseItem?.item?.find((ri) => ri.linkId === addon.linkId);
  const value = nestedResponse?.answer?.[0]?.valueString ?? '';
  const prompt = addon.text?.trim();

  return (
    <Box style={FOLLOW_UP_FIELD_WRAPPER}>
      <Stack gap={4}>
        {prompt ? (
          <Text size="sm" c="dimmed">
            {prompt}
          </Text>
        ) : null}
        <Textarea
          value={value}
          autosize
          minRows={2}
          maxRows={6}
          onChange={(e) => {
            if (!responseItem) return;
            formState.onChangeAnswer(
              [...context, responseItem],
              addon,
              [{ valueString: e.currentTarget.value }],
            );
          }}
          styles={{ input: PREVIEW_INPUT_BG }}
          size="sm"
        />
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OptionAddonFields – follow-up text inputs for dropdown (options not listed)
// ---------------------------------------------------------------------------

function OptionAddonFields({
  formState,
  context,
  item,
  responseItem,
  questionnaireItemsByLinkId,
  selectedAnswer,
  selectedAnswers,
}: {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  responseItem: QuestionnaireResponseItem | undefined;
  questionnaireItemsByLinkId?: Map<string, QuestionnaireItem>;
  /** Current choice label/code from radio or dropdown (preview UI state). */
  selectedAnswer?: string;
  /** Selected option labels for multi-select checkboxes. */
  selectedAnswers?: string[];
}): JSX.Element | null {
  const sourceItem = resolveSourceChoiceItem(item, questionnaireItemsByLinkId);
  const addons = getChoiceOptionAddons(sourceItem);
  if (!addons.length) return null;

  const visibleAddons = addons.filter((addon) =>
    isChoiceOptionAddonVisibleNow(
      sourceItem,
      addon,
      selectedAnswer,
      formState.questionnaireResponse,
      selectedAnswers,
    ),
  );
  if (!visibleAddons.length) return null;

  return (
    <Stack gap="xs" mt={4}>
      {visibleAddons.map((addon) => {
        const nestedResponse = responseItem?.item?.find((ri) => ri.linkId === addon.linkId);
        const value = nestedResponse?.answer?.[0]?.valueString ?? '';
        const prompt = addon.text?.trim();
        return (
          <Stack key={addon.linkId} gap={4}>
            {prompt ? (
              <Text size="sm" c="dimmed">
                {prompt}
              </Text>
            ) : null}
            <Textarea
              value={value}
              autosize
              minRows={2}
              maxRows={6}
              onChange={(e) => {
                if (!responseItem) return;
                formState.onChangeAnswer(
                  [...context, responseItem],
                  addon,
                  [{ valueString: e.currentTarget.value }],
                );
              }}
              styles={{ input: PREVIEW_INPUT_BG }}
              size="sm"
            />
          </Stack>
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// PreviewFormItem – single question input (no medplum-components dependency)
// ---------------------------------------------------------------------------

interface PreviewFormItemProps {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  responseItem: QuestionnaireResponseItem | undefined;
  index: number;
  questionnaireItemsByLinkId?: Map<string, QuestionnaireItem>;
  readOnly?: boolean;
  patientId?: string;
}

/** Preview-only attachment input; keep bytes inline so previews never orphan a Binary. */
function AttachmentFormItem({
  onChange,
}: {
  onChange: (answer: QuestionnaireResponseItem['answer']) => void;
}): JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.readAsDataURL(file);
      });
      onChange([{
        valueAttachment: {
          data,
          title: file.name,
          contentType: file.type || 'application/octet-stream',
        },
      }]);
    } catch {
      // Upload failed — keep the filename so required validation is not blocked
      onChange([{ valueString: file.name }]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      component="label"
      style={{
        display: 'block',
        padding: 'var(--mantine-spacing-lg)',
        borderRadius: 'var(--mantine-radius-md)',
        border: '2px dashed var(--mantine-color-gray-5)',
        backgroundColor: 'var(--mantine-color-white)',
        cursor: uploading ? 'wait' : 'pointer',
        textAlign: 'center',
        transition: 'border-color 0.15s ease',
      }}
    >
      <input type="file" onChange={(e) => { void handleFile(e); }} style={{ display: 'none' }} />
      <Stack gap="xs" align="center">
        <IconUpload size={32} style={{ color: 'var(--mantine-color-gray-5)' }} />
        <Text size="sm" fw={500} c="gray.8">
          {uploading ? 'Uploading…' : (fileName ?? 'Drop files here or click to upload')}
        </Text>
        {!fileName && !uploading && (
          <Text size="xs" c="dimmed">PDF, PNG, JPG up to 10MB</Text>
        )}
      </Stack>
    </Box>
  );
}

function choiceAnswerStrings(
  answers: QuestionnaireResponseItem['answer'] | undefined,
): string[] {
  return (answers ?? [])
    .map((a) => a.valueString ?? a.valueCoding?.display ?? a.valueCoding?.code ?? '')
    .filter(Boolean) as string[];
}

function choiceStringsKey(strings: readonly string[]): string {
  return strings.join('\u0001');
}

/** Read-only dateTime display: local date AND time (never truncated to YYYY-MM-DD). */
export function formatReadOnlyDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function PreviewFormItem({
  formState,
  context,
  item,
  responseItem,
  index,
  questionnaireItemsByLinkId,
  readOnly = false,
  patientId,
}: PreviewFormItemProps): JSX.Element {
  const control = getItemControl(item);
  const options = (item.answerOption ?? []).map((o) => getAnswerOptionDisplayLabel(o));
  const answer = responseItem?.answer?.[index];
  const valueString = answer?.valueString ?? answer?.valueUri ?? answer?.valueCoding?.display ?? answer?.valueCoding?.code ?? '';
  const valueMulti = choiceAnswerStrings(responseItem?.answer);
  const valueMultiKey = choiceStringsKey(valueMulti);

  /** Tracks single-select UI choice immediately — response state can lag one render behind. */
  const [selectedChoice, setSelectedChoice] = useState(valueString);
  useEffect(() => {
    setSelectedChoice((prev) => (prev === valueString ? prev : valueString));
  }, [valueString]);

  /** Tracks multi-select UI choices immediately — response state can lag one render behind. */
  const [selectedChoices, setSelectedChoices] = useState(valueMulti);
  const valueMultiRef = useRef(valueMulti);
  valueMultiRef.current = valueMulti;
  useEffect(() => {
    setSelectedChoices((prev) => (choiceStringsKey(prev) === valueMultiKey ? prev : valueMultiRef.current));
  }, [valueMultiKey]);

  /** Open-choice state for the free-text "Other" field. */
  const [openChoiceOther, setOpenChoiceOther] = useState('');

  const onChange = (newAnswer: QuestionnaireResponseItem['answer']) => {
    if (readOnly) return;
    if (newAnswer) formState.onChangeAnswer(context, item, newAnswer);
  };

  if (control === CHART_CONTACT_PICKER_ITEM_CONTROL && patientId) {
    const phoneItem = questionnaireItemsByLinkId?.get(COORDINATION_OF_CARE_ROI_LINK_IDS.phone);
    return (
      <ChartContactPickerField
        formState={formState}
        context={context}
        item={item}
        responseItem={responseItem}
        patientId={patientId}
        readOnly={readOnly}
        onPhoneAutoFill={
          phoneItem && !readOnly
            ? (phone) => formState.onChangeAnswer(context, phoneItem, [{ valueString: phone }])
            : undefined
        }
      />
    );
  }

  const textFieldKey = buildQuestionnaireTextFieldKey(context, item.linkId, index);
  const isOpenChoice = item.type === QuestionnaireItemType.openChoice;

  if (readOnly) {
    const htmlAnswer = valueString.trim();
    if (item.type === QuestionnaireItemType.text || looksLikeHtml(htmlAnswer)) {
      if (!htmlAnswer) {
        return (
          <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
            —
          </Text>
        );
      }
      return (
        <Box
          className="rich-text-preview"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlAnswer) }}
          style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6, color: 'var(--mantine-color-gray-8)' }}
        />
      );
    }

    const display = valueMulti.length > 0
      ? valueMulti.join(', ')
      : answer?.valueDate
        ? answer.valueDate
        : answer?.valueDateTime
          ? formatReadOnlyDateTime(answer.valueDateTime)
          : answer?.valueBoolean != null
            ? (answer.valueBoolean ? 'Yes' : 'No')
            : valueString;
    return (
      <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
        {display || '—'}
      </Text>
    );
  }

  // Choice / open-choice: drop-down
  if ((item.type === QuestionnaireItemType.choice || isOpenChoice) && control === 'drop-down') {
    return (
      <Stack gap="xs">
        <Select
          data={options}
          value={valueString || null}
          onChange={(v) => {
            setSelectedChoice(v ?? '');
            onChange(v ? [buildChoiceAnswerFromLabel(item, v)] : []);
          }}
          clearable
          styles={{ input: PREVIEW_INPUT_BG }}
        />
        {isOpenChoice && (
          <TextInput
            value={openChoiceOther}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setOpenChoiceOther(next);
              setSelectedChoice(next);
              if (next) {
                onChange([{ valueString: next }]);
              }
            }}
            styles={{ input: PREVIEW_INPUT_BG }}
          />
        )}
        <OptionAddonFields
          formState={formState}
          context={context}
          item={item}
          responseItem={responseItem}
          questionnaireItemsByLinkId={questionnaireItemsByLinkId}
          selectedAnswer={selectedChoice || undefined}
        />
      </Stack>
    );
  }

  // Choice / open-choice: multi-select checkboxes.
  // Default (bare repeats or `check-box-grid`) wraps into columns; explicit `check-box` stays single-column.
  if (
    (item.type === QuestionnaireItemType.choice || isOpenChoice) &&
    (item.repeats || control === 'check-box' || control === 'check-box-grid')
  ) {
    const checkboxes = options.map((o, optionIndex) => (
      <Stack key={o} gap={4}>
        <Checkbox
          value={o}
          label={o}
          color="gold"
          radius="sm"
          styles={{
            input: {
              borderColor: 'var(--mantine-color-gray-5)',
              cursor: 'pointer',
            },
            label: {
              color: 'var(--mantine-color-dark-6)',
              paddingLeft: 8,
            },
          }}
        />
        <OptionAddonField
          formState={formState}
          context={context}
          item={item}
          responseItem={responseItem}
          questionnaireItemsByLinkId={questionnaireItemsByLinkId}
          optionIndex={optionIndex}
          selectedAnswers={selectedChoices}
        />
      </Stack>
    ));
    return (
      <Stack gap={6}>
        <Checkbox.Group
          value={valueMulti}
          onChange={(v) => {
            setSelectedChoices(v);
            onChange(buildChoiceAnswersFromLabels(item, v));
          }}
        >
          {control === 'check-box' ? (
            <Stack gap="xs">{checkboxes}</Stack>
          ) : (
            <Box className="questionnaire-checkbox-columns">{checkboxes}</Box>
          )}
        </Checkbox.Group>
        {isOpenChoice && (
          <Group gap="xs" wrap="nowrap" align="center" mt={4}>
            <Checkbox
              disabled={!openChoiceOther}
              checked={!!openChoiceOther}
              size="sm"
              color="gold"
              readOnly
              styles={{ input: { cursor: 'default' } }}
            />
            <TextInput
              value={openChoiceOther}
              onChange={(e) => {
                const next = e.currentTarget.value;
                setOpenChoiceOther(next);
                if (next) {
                  const existing = valueMulti.filter((v) => options.includes(v));
                  const nextSelected = [...existing, next];
                  setSelectedChoices(nextSelected);
                  onChange([
                    ...buildChoiceAnswersFromLabels(item, existing),
                    { valueString: next },
                  ]);
                }
              }}
              styles={{ input: PREVIEW_INPUT_BG }}
              size="sm"
              style={{ flex: 1 }}
            />
          </Group>
        )}
      </Stack>
    );
  }

  // Choice / open-choice: radio (single-select) — vertical gold radios, "Other" always last
  if (item.type === QuestionnaireItemType.choice || isOpenChoice) {
    const OTHER_VALUE = '__open-choice-other__';
    const isOtherSelected = !!openChoiceOther && valueString === openChoiceOther;
    return (
      <Stack gap={6}>
        <Radio.Group
          value={isOtherSelected ? OTHER_VALUE : valueString}
          onChange={(v) => {
            if (v === OTHER_VALUE) {
              if (openChoiceOther) {
                setSelectedChoice(openChoiceOther);
                onChange([{ valueString: openChoiceOther }]);
              }
            } else {
              setSelectedChoice(v);
              onChange([buildChoiceAnswerFromLabel(item, v)]);
            }
          }}
        >
          <Stack gap="xs">
            {options.map((o, optionIndex) => (
              <Stack key={o} gap={4}>
                <Radio
                  value={o}
                  label={o}
                  color="gold"
                  styles={{
                    radio: {
                      cursor: 'pointer',
                      borderColor: 'var(--mantine-color-gray-5)',
                    },
                    label: {
                      color: 'var(--mantine-color-dark-6)',
                      paddingLeft: 8,
                    },
                  }}
                />
                <OptionAddonField
                  formState={formState}
                  context={context}
                  item={item}
                  responseItem={responseItem}
                  questionnaireItemsByLinkId={questionnaireItemsByLinkId}
                  optionIndex={optionIndex}
                  selectedAnswer={selectedChoice || undefined}
                />
              </Stack>
            ))}
            {isOpenChoice && (
              <Group gap="xs" wrap="nowrap" align="center">
                <Radio
                  value={OTHER_VALUE}
                  label=""
                  color="gold"
                  styles={{ label: { display: 'none' } }}
                />
                <TextInput
                  value={openChoiceOther}
                  onFocus={() => {
                    setSelectedChoice(openChoiceOther);
                    onChange(openChoiceOther ? [{ valueString: openChoiceOther }] : []);
                  }}
                  onChange={(e) => {
                    const next = e.currentTarget.value;
                    setOpenChoiceOther(next);
                    setSelectedChoice(next);
                    if (next) {
                      onChange([{ valueString: next }]);
                    }
                  }}
                  styles={{ input: PREVIEW_INPUT_BG }}
                  size="sm"
                  style={{ flex: 1 }}
                />
              </Group>
            )}
          </Stack>
        </Radio.Group>
      </Stack>
    );
  }

  // Text (long text) — rich text editor with undo
  if (item.type === QuestionnaireItemType.text) {
    return (
      <QuestionnaireTextEntry
        fieldKey={textFieldKey}
        mode="rich"
        value={valueString}
        onChange={(html) => onChange([{ valueString: html }])}
        styles={{ input: PREVIEW_INPUT_BG }}
        placeholder={PREVIEW_TEXTAREA_PLACEHOLDER}
      />
    );
  }

  // String — plain text with undo
  if (item.type === QuestionnaireItemType.string) {
    return (
      <QuestionnaireTextEntry
        fieldKey={textFieldKey}
        mode="plain"
        value={valueString}
        onChange={(next) => onChange([{ valueString: next }])}
        styles={{ input: PREVIEW_INPUT_BG }}
        placeholder={PREVIEW_TEXTAREA_PLACEHOLDER}
      />
    );
  }

  // Decimal / Integer
  if (item.type === QuestionnaireItemType.decimal || item.type === QuestionnaireItemType.integer) {
    const numVal = answer?.valueDecimal ?? answer?.valueInteger;
    return (
      <NumberInput
        value={numVal ?? ''}
        styles={{ input: PREVIEW_INPUT_BG }}
        allowDecimal={item.type === QuestionnaireItemType.decimal}
        onChange={(v) => {
          if (v === '' || v === undefined) {
            onChange([]);
          } else if (item.type === QuestionnaireItemType.integer) {
            onChange([{ valueInteger: Math.round(Number(v)) }]);
          } else {
            onChange([{ valueDecimal: Number(v) }]);
          }
        }}
      />
    );
  }

  // Date — local calendar day preserved (UnifyDateInput)
  if (item.type === QuestionnaireItemType.date) {
    const v = answer?.valueDate ?? '';
    return (
      <UnifyDateInput
        value={v || null}
        onChange={(iso) => {
          if (iso) {
            onChange([{ valueDate: iso }]);
          } else {
            onChange([]);
          }
        }}
        clearable={!readOnly}
        readOnly={readOnly}
        disabled={readOnly}
        leftSection={<IconCalendarEvent size={16} />}
        styles={{ input: PREVIEW_INPUT_BG }}
      />
    );
  }

  // DateTime — Mantine DateTimePicker
  if (item.type === QuestionnaireItemType.dateTime) {
    const v = answer?.valueDateTime ?? '';
    return (
      <DateTimePicker
        value={parseFhirDateOrInstant(v)}
        onChange={(d) => {
          // Mantine v8 emits a local wall-clock 'YYYY-MM-DD HH:mm:ss' string; parse its parts
          // explicitly instead of relying on engine-specific `new Date(string)` behavior.
          const dateObj = calendarDateFromPickerValue(d);
          if (dateObj) {
            const time = /[ T](\d{2}):(\d{2})/.exec(String(d));
            if (time) dateObj.setHours(Number(time[1]), Number(time[2]), 0, 0);
            onChange([{ valueDateTime: dateObj.toISOString() }]);
          } else {
            onChange([]);
          }
        }}
        clearable
        leftSection={<IconCalendarEvent size={16} />}
        styles={{ input: PREVIEW_INPUT_BG }}
      />
    );
  }

  // Time — Mantine TimePicker
  if (item.type === QuestionnaireItemType.time) {
    const v = answer?.valueTime ?? answer?.valueString ?? '';
    return (
      <TimePicker
        value={v || undefined}
        onChange={(val) => {
          const fhirTime = toFhirTimeValue(val);
          onChange(fhirTime ? [{ valueTime: fhirTime }] : []);
        }}
        format="12h"
        withDropdown
        leftSection={<IconClock size={16} />}
        styles={{ input: PREVIEW_INPUT_BG }}
      />
    );
  }

  // Attachment — file upload dropzone
  if (item.type === QuestionnaireItemType.attachment) {
    return <AttachmentFormItem onChange={onChange} />;
  }

  // URL — text input with URL type
  if (item.type === QuestionnaireItemType.url) {
    return (
      <TextInput
        type="url"
        value={valueString}
        onChange={(e) => onChange([{ valueUri: e.currentTarget.value }])}
        styles={{ input: PREVIEW_INPUT_BG }}
      />
    );
  }

  // Quantity — value + unit side by side (Medplum pattern)
  if (item.type === QuestionnaireItemType.quantity) {
    const qtyValue = answer?.valueQuantity?.value;
    const qtyUnit = answer?.valueQuantity?.unit ?? '';
    return (
      <Group gap="sm" grow>
        <NumberInput
          value={qtyValue ?? ''}
          onChange={(v) => {
            onChange([{
              valueQuantity: {
                value: v === '' || v === undefined ? undefined : Number(v),
                unit: qtyUnit || undefined,
              },
            }]);
          }}
          styles={{ input: PREVIEW_INPUT_BG }}
        />
        <TextInput
          value={qtyUnit}
          onChange={(e) => {
            onChange([{
              valueQuantity: {
                value: qtyValue,
                unit: e.currentTarget.value || undefined,
              },
            }]);
          }}
          styles={{ input: PREVIEW_INPUT_BG }}
        />
      </Group>
    );
  }

  // Fallback
  return (
    <TextInput
      value={valueString}
      onChange={(e) => onChange([{ valueString: e.currentTarget.value }])}
      styles={{ input: PREVIEW_INPUT_BG }}
    />
  );
}

/** Returns true if any item in the tree has required === true */
function hasRequiredItem(items: QuestionnaireItem[] | undefined): boolean {
  if (!items?.length) return false;
  return items.some(
    (item) => !!item.required || hasRequiredItem(item.item),
  );
}

// ---------------------------------------------------------------------------
// FormTitleBlock – title block with gold bar (matches editor/preview/forms)
// ---------------------------------------------------------------------------

function FormTitleBlock({
  title,
  description,
  showRequiredHint = false,
}: {
  title: string;
  description?: string;
  showRequiredHint?: boolean;
}): JSX.Element {
  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden', backgroundColor: 'var(--mantine-color-white)' }}>
      <Box
        style={{
          height: 12,
          backgroundColor: 'var(--mantine-color-gold-5)',
          width: '100%',
        }}
      />
      <Group justify="space-between" align="flex-start" wrap="nowrap" p="lg" gap="md">
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text c="dark" style={{ fontSize: '1.75rem', lineHeight: 1.3 }}>
            {title || 'Form title'}
          </Text>
          {description && (
            <Text size="sm" c="dimmed" mt="xs">
              {description}
            </Text>
          )}
        </Stack>
        {showRequiredHint && (
          <Text size="xs" c="red" style={{ flexShrink: 0 }}>
            * indicates a required field
          </Text>
        )}
      </Group>
    </Paper>
  );
}

// PreviewSignatureSection – interactive signature (shared client portal block)
function PreviewSignatureSection(): JSX.Element {
  const [signature, setSignature] = useState<SignatureInfo | null>(null);
  return (
    <ClientPortalSignatureSection
      signature={signature}
      onSignatureChange={setSignature}
    />
  );
}

// ---------------------------------------------------------------------------
// PreviewFormContent – Medplum-backed form rendering (exported for editor)
// ---------------------------------------------------------------------------

export interface PreviewFormContentProps {
  questionnaire: Questionnaire;
  /** When true, fields render read-only (e.g. provider viewing a pending client form). */
  readOnly?: boolean;
}

export function PreviewFormContent({ questionnaire, readOnly = false }: PreviewFormContentProps): JSX.Element {
  const docFormat = useDocumentFormatPresentation();
  // Substitute `{{namespace.field}}` tokens before handing the template to
  // the runtime renderer. Unresolved tokens are left visible in the preview
  // so admins can see what they still need to wire up.
  const { context: varCtx } = useVariableContext();
  const resolved = resolveQuestionnaireVariables(questionnaire, varCtx, {
    onUnresolved: 'token',
  });

  const previewDefaultResponse = useMemo((): QuestionnaireResponse | undefined => {
    if (!questionnaireHasLinkId(resolved.item, SESSION_MODALITY_LINK_ID)) return undefined;
    return {
      resourceType: 'QuestionnaireResponse',
      status: 'in-progress',
      item: upsertSessionModalityAnswer([], 'hybrid'),
    };
  }, [resolved]);

  const formState = useQuestionnaireForm({
    questionnaire: resolved,
    defaultValue: previewDefaultResponse,
    disablePagination: false,
  });

  const formTitle = resolved.title ?? '';
  const formDescription = resolved.description ?? '';
  const showRequiredHint = hasRequiredItem(resolved.item);
  const signatureRequired = hasSignatureRequired(questionnaire);

  const wrapPreviewBody = (content: JSX.Element): JSX.Element => (
    <Stack gap="md">
      {content}
      <DocumentFormatFooter footerText={docFormat.footerText} />
    </Stack>
  );

  // One card for the whole body — same as the client portal and chart document page.
  const bodyCard = (content: ReactNode): JSX.Element => (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">{content}</Stack>
    </Paper>
  );

  if (formState.loading) {
    return wrapPreviewBody(
      <Paper withBorder p="lg" ta="center">
        <Text size="sm" c="dimmed">Loading preview…</Text>
      </Paper>,
    );
  }

  if (formState.items.length === 0) {
    return wrapPreviewBody(
      <Stack gap="md">
        <FormTitleBlock title={formTitle} description={formDescription} showRequiredHint={showRequiredHint} />
        <Paper withBorder p="lg" ta="center">
          <Text size="sm" c="dimmed">
            This template has no items to preview.
          </Text>
        </Paper>
      </Stack>,
    );
  }

  if (formState.pagination) {
    const { pages, activePage, onNextPage, onPrevPage } = formState;
    const showBack = activePage > 0;
    const showNext = activePage < pages.length - 1;

    return wrapPreviewBody(
      <form onSubmit={(e) => e.preventDefault()}>
        <Stack gap="md">
          <FormTitleBlock title={formTitle} description={formDescription} showRequiredHint={showRequiredHint} />
          {bodyCard(
            <>
              <Stepper active={activePage} allowNextStepsSelect={false} size="sm">
                {pages.map((page, pageIndex) => (
                  <Stepper.Step key={page.linkId} label={page.title}>
                    {pageIndex === activePage && (
                      <PreviewItemArray
                        formState={formState}
                        context={[]}
                        items={formState.items}
                        responseItems={formState.responseItems}
                        questionnaireItems={resolved.item}
                        variableContext={varCtx}
                        readOnly={readOnly}
                      />
                    )}
                  </Stepper.Step>
                ))}
              </Stepper>
              <Group justify="center" gap="sm" mt="md">
                {showBack && (
                  <Button variant="default" size="sm" onClick={onPrevPage}>
                    Back
                  </Button>
                )}
                {showNext && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      const form = e.currentTarget.closest('form') as HTMLFormElement;
                      if (form.reportValidity()) {
                        onNextPage();
                      }
                    }}
                  >
                    Next
                  </Button>
                )}
              </Group>
              {signatureRequired && <PreviewSignatureSection />}
            </>,
          )}
        </Stack>
      </form>,
    );
  }

  // Single page — no pagination
  return wrapPreviewBody(
    <form onSubmit={(e) => e.preventDefault()}>
      <Stack gap="md">
        <FormTitleBlock title={formTitle} description={formDescription} showRequiredHint={showRequiredHint} />
        {bodyCard(
          <>
            <PreviewItemArray
              formState={formState}
              context={[]}
              items={formState.items}
              responseItems={formState.responseItems}
              questionnaireItems={resolved.item}
              variableContext={varCtx}
              readOnly={readOnly}
            />
            {signatureRequired && <PreviewSignatureSection />}
          </>,
        )}
      </Stack>
    </form>,
  );
}

// ---------------------------------------------------------------------------
// PreviewItemArray – flat form field list (no nested cards)
// ---------------------------------------------------------------------------

export interface PreviewItemArrayProps {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  items: QuestionnaireItem[];
  responseItems: QuestionnaireResponseItem[];
  /** Full questionnaire item tree — used to resolve nested per-option add-ons. */
  questionnaireItems?: QuestionnaireItem[];
  /** When provided, render review comments under each item by itemPath (from supervision). */
  commentsByPath?: Record<string, DocumentComment[]>;
  onResolveComment?: (commentId: string) => void;
  /** When true, show Resolve on unresolved comments (e.g. edit page). */
  canResolve?: boolean;
  /** Variable context for resolving {{tokens}} in rich-text block content. */
  variableContext?: import('../../../../lib/template-variables/registry').VariableContext;
  /** When set, fields with these linkIds render read-only (e.g. provider-locked chart document fields). */
  readOnlyLinkIds?: ReadonlySet<string>;
  /** When true, all editable fields render read-only. */
  readOnly?: boolean;
  /** Patient id for chart contact picker fields (client portal / chart documents). */
  patientId?: string;
  /** Nesting depth for tree-style group indent (0 = root). */
  depth?: number;
}

function wrapWithComments(
  listKey: string,
  itemPath: string,
  node: ReactNode,
  commentsByPath?: Record<string, DocumentComment[]>,
  onResolveComment?: (commentId: string) => void,
  canResolve?: boolean,
): ReactNode {
  const thread = commentsByPath?.[itemPath];
  const anchored = (
    <Box data-item-path={itemPath} className="questionnaire-item-anchor">
      {node}
    </Box>
  );
  if (!thread?.length) {
    return <Fragment key={listKey}>{anchored}</Fragment>;
  }
  return (
    <Stack key={listKey} gap="xs">
      {anchored}
      <CommentThreadBlock
        itemPath={itemPath}
        comments={thread}
        onResolveComment={onResolveComment}
        canResolve={canResolve}
      />
    </Stack>
  );
}

/**
 * Visibility outcomes for conditional descendants of a row (enableWhen).
 * Conditional logic can reference answers anywhere in the response, so the
 * row's own serialized content is not enough to know when to re-render.
 */
function buildConditionalVisibilityKey(
  item: QuestionnaireItem,
  questionnaireResponse: QuestionnaireFormLoadedState['questionnaireResponse'],
): string {
  let key = '';
  const walk = (nodes: QuestionnaireItem[] | undefined): void => {
    for (const node of nodes ?? []) {
      if (node.enableWhen?.length) {
        key += isQuestionEnabled(node, questionnaireResponse) ? '1' : '0';
      }
      walk(node.item);
    }
  };
  walk(item.item);
  return key;
}

export function PreviewItemArray({
  formState,
  context,
  items,
  responseItems,
  questionnaireItems,
  commentsByPath,
  onResolveComment,
  canResolve = false,
  variableContext,
  readOnlyLinkIds,
  readOnly = false,
  patientId,
  depth = 0,
}: PreviewItemArrayProps): JSX.Element {
  const parentPath = context.map((r) => r.linkId).filter(Boolean).join('/');
  const questionnaireItemsByLinkId = useMemo(
    () => buildQuestionnaireItemMap(questionnaireItems),
    [questionnaireItems],
  );

  return (
    <Stack gap="lg">
      {items.map((item, index) => {
        if (isHiddenQuestionnaireItem(item)) {
          return null;
        }
        // Date Signed is stamped by form-submission bot — hide while filling.
        if (
          !readOnly &&
          item.type === QuestionnaireItemType.date &&
          isSignatureDateQuestion(item)
        ) {
          return null;
        }
        if (!isQuestionEnabled(item, formState.questionnaireResponse)) {
          return null;
        }

        const itemPath = buildItemPath(item.linkId, parentPath);
        const rowKey = `${parentPath || 'root'}__${item.linkId ?? 'x'}__${index}`;
        const filteredResponseItems = responseItems.filter((ri) => ri.linkId === item.linkId);

        return (
          <MemoPreviewItemRow
            key={rowKey}
            formState={formState}
            context={context}
            item={item}
            index={index}
            itemPath={itemPath}
            rowKey={rowKey}
            filteredResponseItems={filteredResponseItems}
            questionnaireItems={questionnaireItems}
            questionnaireItemsByLinkId={questionnaireItemsByLinkId}
            commentsByPath={commentsByPath}
            onResolveComment={onResolveComment}
            canResolve={canResolve}
            variableContext={variableContext}
            readOnlyLinkIds={readOnlyLinkIds}
            readOnly={readOnly}
            patientId={patientId}
            depth={depth}
            responseContentKey={JSON.stringify(filteredResponseItems)}
            nestedEnabledKey={buildConditionalVisibilityKey(item, formState.questionnaireResponse)}
          />
        );
      })}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// PreviewItemRow – one form row, memoized by content
//
// `useQuestionnaireForm` mutates its QuestionnaireResponse in place and
// force-renders the whole form on every keystroke, so without memoization
// every Mantine input and Tiptap editor reconciles on each edit (the lag).
// Identity comparison is useless against in-place mutation, so the memo
// compares serialized row content + conditional-visibility outcomes instead.
// Skipping renders with a stale `formState`/`context` is safe: the hook's
// callbacks read live data through an internal ref, and the response item
// objects keep stable identities across edits.
// ---------------------------------------------------------------------------

interface PreviewItemRowProps {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  index: number;
  itemPath: string;
  rowKey: string;
  filteredResponseItems: QuestionnaireResponseItem[];
  questionnaireItems?: QuestionnaireItem[];
  questionnaireItemsByLinkId: Map<string, QuestionnaireItem>;
  commentsByPath?: Record<string, DocumentComment[]>;
  onResolveComment?: (commentId: string) => void;
  canResolve: boolean;
  variableContext?: import('../../../../lib/template-variables/registry').VariableContext;
  readOnlyLinkIds?: ReadonlySet<string>;
  readOnly?: boolean;
  depth?: number;
  patientId?: string;
  /** Serialized response subtree for this row — memo invalidation signal. */
  responseContentKey: string;
  /** enableWhen outcomes for descendants — memo invalidation signal. */
  nestedEnabledKey: string;
}

function previewItemRowPropsEqual(
  prev: PreviewItemRowProps,
  next: PreviewItemRowProps,
): boolean {
  return (
    prev.item === next.item &&
    prev.index === next.index &&
    prev.itemPath === next.itemPath &&
    prev.responseContentKey === next.responseContentKey &&
    prev.nestedEnabledKey === next.nestedEnabledKey &&
    prev.commentsByPath === next.commentsByPath &&
    prev.onResolveComment === next.onResolveComment &&
    prev.canResolve === next.canResolve &&
    prev.variableContext === next.variableContext &&
    prev.questionnaireItems === next.questionnaireItems &&
    prev.questionnaireItemsByLinkId === next.questionnaireItemsByLinkId &&
    prev.depth === next.depth
    // formState, context, filteredResponseItems intentionally not compared —
    // see component comment above.
  );
}

const MemoPreviewItemRow = memo(PreviewItemRow, previewItemRowPropsEqual);

function PreviewItemRow({
  formState,
  context,
  item,
  index,
  itemPath,
  rowKey,
  filteredResponseItems,
  questionnaireItems,
  questionnaireItemsByLinkId,
  commentsByPath,
  onResolveComment,
  canResolve,
  variableContext,
  readOnlyLinkIds,
  readOnly = false,
  depth = 0,
  patientId,
}: PreviewItemRowProps): JSX.Element {
  const fieldReadOnly = readOnly || Boolean(item.linkId && readOnlyLinkIds?.has(item.linkId));
  const row = ((): ReactNode => {
        // Rich text block — static HTML content block
        if (item.type === QuestionnaireItemType.text && getItemControl(item) === 'rich-text') {
          const rawHtml = item.initial?.[0]?.valueString ?? '';
          const htmlContent = rawHtml
            ? (variableContext
                ? resolveVariablesInText(rawHtml, variableContext, { onUnresolved: 'token' })
                : rawHtml)
            : '';
          return wrapWithComments(
            rowKey,
            itemPath,
            htmlContent ? (
              <Box
                key={`richtext-${item.id}-${index}`}
                className="rich-text-preview"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6 }}
              />
            ) : (
              <Box key={`richtext-${item.id}-${index}`} />
            ),
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Display items (heading, separator, plain text)
        if (item.type === QuestionnaireItemType.display) {
          const control = getItemControl(item);
          const displayKey = `display-${item.id}-${index}`;
          if (control === 'separator') {
            return <Divider key={displayKey} />;
          }
          const displayContent =
            control === 'header' ? (
              <Text fw={700} c="dark" size="lg">
                {item.text}
              </Text>
            ) : (
              <Text fw={700} c="dark" size="md">
                {item.text}
              </Text>
            );
          return wrapWithComments(
            rowKey,
            itemPath,
            <Box key={displayKey}>{displayContent}</Box>,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Repeatable group
        if (item.type === QuestionnaireItemType.group && item.repeats) {
          return wrapWithComments(
            rowKey,
            itemPath,
            <Stack key={`rep-group-${item.id}-${index}`} gap="lg">
              {filteredResponseItems.map((responseItem, instanceIndex) => (
                <PreviewGroup
                  key={`group-${responseItem.id ?? instanceIndex}`}
                  formState={formState}
                  context={context}
                  item={item}
                  responseItem={responseItem}
                  questionnaireItems={questionnaireItems}
                  commentsByPath={commentsByPath}
                  onResolveComment={onResolveComment}
                  canResolve={canResolve}
                  variableContext={variableContext}
                  readOnlyLinkIds={readOnlyLinkIds}
                  readOnly={readOnly}
                  depth={depth}
                  instanceIndex={instanceIndex}
                  patientId={patientId}
                />
              ))}
              {!fieldReadOnly && (
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => formState.onAddGroup(context, item)}
              >
                Add {item.text ?? 'group'}
              </Button>
              )}
            </Stack>,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Group
        if (item.type === QuestionnaireItemType.group) {
          return wrapWithComments(
            rowKey,
            itemPath,
            <PreviewGroup
              key={`group-${item.id}-${index}`}
              formState={formState}
              context={context}
              item={item}
              responseItem={filteredResponseItems[0]}
              questionnaireItems={questionnaireItems}
              commentsByPath={commentsByPath}
              onResolveComment={onResolveComment}
              canResolve={canResolve}
              variableContext={variableContext}
              readOnlyLinkIds={readOnlyLinkIds}
              readOnly={readOnly}
              depth={depth}
              patientId={patientId}
            />,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Boolean
        if (item.type === QuestionnaireItemType.boolean) {
          const responseItem = filteredResponseItems[0];
          const currentVal = responseItem?.answer?.[0]?.valueBoolean ?? false;
          return wrapWithComments(
            rowKey,
            itemPath,
            <PreviewQuestionShell key={`bool-${item.id}-${index}`} label={item.text ?? ''} required={item.required}>
              <Checkbox
                label="Yes"
                checked={currentVal}
                onChange={(e) => {
                  formState.onChangeAnswer(context, item, [{ valueBoolean: e.currentTarget.checked }]);
                }}
                size="sm"
                readOnly={fieldReadOnly}
                disabled={fieldReadOnly}
              />
            </PreviewQuestionShell>,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Repeating choice items: render once (checkbox/dropdown handles multi-select internally)
        if (item.repeats && (item.type === QuestionnaireItemType.choice || item.type === QuestionnaireItemType.openChoice)) {
          return wrapWithComments(
            rowKey,
            itemPath,
            <PreviewQuestionShell key={`choice-${item.id}-${index}`} label={item.text ?? ''} required={item.required}>
              <PreviewFormItem
                formState={formState}
                context={context}
                item={item}
                responseItem={filteredResponseItems[0]}
                index={0}
                questionnaireItemsByLinkId={questionnaireItemsByLinkId}
                readOnly={fieldReadOnly}
                patientId={patientId}
              />
            </PreviewQuestionShell>,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Repeating non-choice items
        if (item.repeats) {
          const responseItem = filteredResponseItems[0];
          const answers = responseItem?.answer && responseItem.answer.length > 0 ? responseItem.answer : [{}];
          return wrapWithComments(
            rowKey,
            itemPath,
            <PreviewQuestionShell key={`repeat-${item.id}-${index}`} label={item.text ?? ''} required={item.required}>
              <Stack gap="xs">
                {answers.map((_, answerIndex) => (
                  <PreviewFormItem
                    key={`${item.linkId}-${answerIndex}`}
                    formState={formState}
                    context={context}
                    item={item}
                    responseItem={responseItem}
                    index={answerIndex}
                    questionnaireItemsByLinkId={questionnaireItemsByLinkId}
                    readOnly={fieldReadOnly}
                    patientId={patientId}
                  />
                ))}
              </Stack>
              {!fieldReadOnly && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => formState.onAddAnswer(context, item)}
                >
                  Add Item
                </Button>
              )}
            </PreviewQuestionShell>,
            commentsByPath,
            onResolveComment,
            canResolve,
          );
        }

        // Standard question item (non-boolean, non-repeating)
        return wrapWithComments(
          rowKey,
          itemPath,
          <PreviewQuestionShell key={`item-${item.id}-${index}`} label={item.text ?? ''} required={item.required}>
            <PreviewFormItem
              formState={formState}
              context={context}
              item={item}
              responseItem={filteredResponseItems[0]}
              index={0}
              questionnaireItemsByLinkId={questionnaireItemsByLinkId}
              readOnly={fieldReadOnly}
              patientId={patientId}
            />
          </PreviewQuestionShell>,
          commentsByPath,
          onResolveComment,
          canResolve,
        );
      })();

  return <>{row}</>;
}

// ---------------------------------------------------------------------------
// PreviewGroup – collapsible section with flat nested fields
// ---------------------------------------------------------------------------

function PreviewGroup({
  formState,
  context,
  item,
  responseItem,
  questionnaireItems,
  commentsByPath,
  onResolveComment,
  canResolve,
  variableContext,
  readOnlyLinkIds,
  readOnly = false,
  depth = 0,
  instanceIndex,
  patientId,
}: {
  formState: QuestionnaireFormLoadedState;
  context: QuestionnaireResponseItem[];
  item: QuestionnaireItem;
  responseItem: QuestionnaireResponseItem | undefined;
  questionnaireItems?: QuestionnaireItem[];
  commentsByPath?: Record<string, DocumentComment[]>;
  onResolveComment?: (commentId: string) => void;
  canResolve?: boolean;
  variableContext?: import('../../../../lib/template-variables/registry').VariableContext;
  readOnlyLinkIds?: ReadonlySet<string>;
  readOnly?: boolean;
  depth?: number;
  instanceIndex?: number;
  patientId?: string;
}): JSX.Element {
  const groupResponseItem = responseItem ?? { linkId: item.linkId, item: [] };
  const newContext = [...context, groupResponseItem];
  const isPage = getItemControl(item) === 'page';
  const childDepth = depth + 1;
  const children = (
    <PreviewItemArray
      formState={formState}
      context={newContext}
      items={item.item ?? []}
      responseItems={groupResponseItem.item ?? []}
      questionnaireItems={questionnaireItems}
      commentsByPath={commentsByPath}
      onResolveComment={onResolveComment}
      canResolve={canResolve}
      variableContext={variableContext}
      readOnlyLinkIds={readOnlyLinkIds}
      readOnly={readOnly}
      patientId={patientId}
      depth={childDepth}
    />
  );
  if (isPage) return <>{children}</>;
  return (
    <PreviewSectionGroup
      title={item.text ?? ''}
      required={item.required}
      depth={depth}
      instanceIndex={instanceIndex}
    >
      {children}
    </PreviewSectionGroup>
  );
}

// ---------------------------------------------------------------------------
// TemplatePreviewDrawer (main export)
// ---------------------------------------------------------------------------

interface TemplatePreviewDrawerProps {
  opened: boolean;
  onClose: () => void;
  template: TemplateListItem | null;
  /** Called when the user wants to duplicate the template. */
  onDuplicate?: (template: TemplateListItem) => void;
  /** Called when the user wants to archive the template. */
  onArchive?: (template: TemplateListItem) => void;
}

export function TemplatePreviewDrawer({
  opened,
  onClose,
  template,
  onDuplicate,
  onArchive,
}: TemplatePreviewDrawerProps): JSX.Element {
  // Increment key on each drawer open to force PreviewFormContent remount (fresh state)
  const [openKey, setOpenKey] = useState(0);
  useEffect(() => {
    if (opened) setOpenKey((k) => k + 1);
  }, [opened]);

  if (!template) {
    return <Drawer opened={opened} onClose={onClose} position="right" size="calc(100vw - 80px)" />;
  }

  const items = template.resource.item ?? [];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="calc(100vw - 80px)"
      withCloseButton={false}
      styles={{
        body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Sticky header */}
      <Box
        px="lg"
        py="sm"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-5)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Group gap="md" align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Title order={4} style={{ margin: 0 }}>
            {template.title}
          </Title>
          {items.length > 0 && (
            <Text size="xs" c="red">
              * indicates a required field
            </Text>
          )}
        </Group>
        <Group gap="sm">
          {onDuplicate && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              leftSection={<IconCopy size={14} />}
              onClick={() => onDuplicate(template)}
            >
              Duplicate
            </Button>
          )}
          {onArchive && template.status !== 'retired' && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              leftSection={<IconArchive size={14} />}
              onClick={() => onArchive(template)}
            >
              Archive
            </Button>
          )}
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            onClick={onClose}
            aria-label="Close preview"
          >
            <IconX size={18} />
          </Button>
        </Group>
      </Box>

      {/* Scrollable content — padding and gap match theme / StylingPage */}
      <Box style={{ flex: 1, overflowY: 'auto', padding: 'var(--mantine-spacing-lg)' }}>
        <Stack gap="md" maw={740} mx="auto">
          {items.length === 0 ? (
            <Paper withBorder p="lg" ta="center">
              <Text size="sm" c="dimmed">
                This template has no items to preview. Open the Template Editor to add content.
              </Text>
            </Paper>
          ) : (
            <PreviewFormContent key={`${template.id}-${openKey}`} questionnaire={template.resource} />
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
